import "phoenix_html"
import { Socket } from "phoenix"
import { LiveSocket } from "phoenix_live_view"
import topbar from "topbar"
import React from "react"
import { createRoot, type Root } from "react-dom/client"
import { AdminRevenueChart } from "./components/AdminRevenueChart"
import { TrendCompareChart } from "./components/TrendCompareChart"
import { DataTable } from "./components/DataTable"
import { transactionColumns, dashboardTransactionColumns } from "./components/columns/transactions"
import { makeStudentColumns, StudentDetailPanel } from "./components/columns/students"
import { makeVideoColumns } from "./components/columns/videos"
import { makeCouponColumns } from "./components/columns/coupons"
import { Watermark } from "./hooks/Watermark"
import { VideoPlayer } from "./hooks/VideoPlayer"
import { WizardStep } from "./hooks/WizardStep"
import { TipTap } from "./hooks/TipTap"
import { PaystackPopup } from "./hooks/PaystackPopup"
import { AuthCarousel } from "./hooks/AuthCarousel"
import { PreviewTimer } from "./hooks/PreviewTimer"
import { WalletCard } from "./hooks/WalletCard"
import { ThumbnailPreview, ThumbnailReviewSync } from "./hooks/ThumbnailPreview"
import { VideoPreview } from "./hooks/VideoPreview"
import { DraggableTable } from "./hooks/DraggableTable"
import { DismissableNotices } from "./hooks/DismissableNotices"
import { PhoneInputKe } from "./hooks/PhoneInputKe"
import { BunnyTusUpload } from "./hooks/BunnyTusUpload"
import { OTPInput } from "./hooks/OTPInput"
import { DashboardGrid } from "./hooks/DashboardGrid"
import { SelectAll } from "./hooks/SelectAll"

const SidebarCollapse = {
  mounted(this: { el: HTMLElement }) {
    const el = this.el
    if (localStorage.getItem("sidebar:collapsed") === "true") {
      el.dataset.collapsed = ""
    }
    el.addEventListener("sidebar:toggle", () => {
      if ("collapsed" in el.dataset) {
        delete el.dataset.collapsed
        localStorage.setItem("sidebar:collapsed", "false")
      } else {
        el.dataset.collapsed = ""
        localStorage.setItem("sidebar:collapsed", "true")
      }
    })
  },
}

const RevenueChart = {
  _root: null as Root | null,
  mounted(this: typeof RevenueChart & { el: HTMLElement; handleEvent: Function }) {
    this._root = createRoot(this.el)
    this._render()
    // Server pushes chart:update when the filter changes; the element is
    // phx-update="ignore" so updated() won't fire — we listen here instead.
    this.handleEvent("chart:update", ({ data }: { data: string }) => {
      const parsed = JSON.parse(data)
      this._root?.render(React.createElement(AdminRevenueChart, { data: parsed }))
    })
  },
  destroyed(this: typeof RevenueChart) {
    this._root?.unmount()
    this._root = null
  },
  _render(this: typeof RevenueChart & { el: HTMLElement }) {
    const data = JSON.parse(this.el.dataset.chart ?? "[]")
    this._root?.render(React.createElement(AdminRevenueChart, { data }))
  },
}

const TrendChart = {
  _root: null as import("react-dom/client").Root | null,
  mounted(this: typeof TrendChart & { el: HTMLElement; handleEvent: Function }) {
    this._root = createRoot(this.el)
    this._render()
    this.handleEvent("trend:update", ({ data }: { data: string }) => {
      const parsed = JSON.parse(data)
      this._root?.render(React.createElement(TrendCompareChart, { data: parsed }))
    })
  },
  destroyed(this: typeof TrendChart) {
    this._root?.unmount()
    this._root = null
  },
  _render(this: typeof TrendChart & { el: HTMLElement }) {
    const data = JSON.parse(this.el.dataset.chart ?? "[]")
    this._root?.render(React.createElement(TrendCompareChart, { data }))
  },
}

const CountUp = {
  mounted(this: { el: HTMLElement }) { this._run() },
  updated(this: { el: HTMLElement }) { this._run() },
  _run(this: { el: HTMLElement }) {
    const raw = this.el.textContent?.trim() ?? ""
    if (!/^\d+$/.test(raw)) return
    const target = parseInt(raw, 10)
    if (target === 0) return
    const duration = 700
    const start = performance.now()
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1)
      const eased = 1 - (1 - t) ** 3
      this.el.textContent = Math.round(target * eased).toString()
      if (t < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  },
}

const ThemeToggle = {
  mounted(this: { el: HTMLElement }) {
    this._sync()
    this.el.addEventListener("click", () => {
      const isDark = document.documentElement.classList.toggle("dark")
      localStorage.setItem("theme", isDark ? "dark" : "light")
      this._sync()
    })
  },
  _sync(this: { el: HTMLElement }) {
    const isDark = document.documentElement.classList.contains("dark")
    const sun = this.el.querySelector<HTMLElement>(".theme-sun")
    const moon = this.el.querySelector<HTMLElement>(".theme-moon")
    if (sun) sun.style.display = isDark ? "flex" : "none"
    if (moon) moon.style.display = isDark ? "none" : "flex"
  },
}

const AdminDataTable = {
  _root: null as ReturnType<typeof createRoot> | null,
  mounted(this: any) {
    this._root = createRoot(this.el)
    this._render()
    this.handleEvent("table:update", ({ rows }: { rows: string }) => {
      this.el.dataset.rows = rows
      this._render()
    })
  },
  updated(this: any) {
    this._render()
  },
  destroyed(this: any) {
    this._root?.unmount()
    this._root = null
  },
  _render(this: any) {
    const rows = JSON.parse(this.el.dataset.rows ?? "[]")
    const type = this.el.dataset.tableType ?? "transactions"
    const pushEvent = this.pushEvent.bind(this)

    let element: React.ReactElement

    if (type === "dashboard-transactions") {
      element = React.createElement(DataTable, {
        columns: dashboardTransactionColumns,
        data: rows,
        emptyMessage: "No transactions yet.",
        pageSize: 8,
      })
    } else if (type === "transactions") {
      element = React.createElement(DataTable, {
        columns: transactionColumns,
        data: rows,
        searchKey: "student_name",
        searchPlaceholder: "Search by student or video…",
        emptyMessage: "No transactions match these filters.",
        pageSize: 25,
      })
    } else if (type === "students") {
      const columns = makeStudentColumns(pushEvent)
      element = React.createElement(DataTable, {
        columns,
        data: rows,
        searchKey: "full_name",
        searchPlaceholder: "Search by name, email or phone…",
        emptyMessage: "No students found.",
        renderSubRow: (row: any) =>
          React.createElement(StudentDetailPanel, { student: row.original }),
      })
    } else if (type === "videos") {
      const columns = makeVideoColumns(pushEvent)
      element = React.createElement(DataTable, {
        columns,
        data: rows,
        searchKey: "title",
        searchPlaceholder: "Search videos…",
        emptyMessage: "No videos found.",
        pageSize: 25,
      })
    } else if (type === "coupons") {
      const columns = makeCouponColumns(pushEvent)
      element = React.createElement(DataTable, {
        columns,
        data: rows,
        searchKey: "code",
        searchPlaceholder: "Search coupons…",
        emptyMessage: "No coupons found.",
      })
    } else {
      element = React.createElement("p", { className: "p-4 text-sm text-muted-foreground" }, "Unknown table type.")
    }

    this._root?.render(element)
  },
}

const hooks: Record<string, object> = {
  SidebarCollapse,
  RevenueChart,
  TrendChart,
  ThemeToggle,
  CountUp,
  Watermark,
  VideoPlayer,
  WizardStep,
  TipTap,
  PaystackPopup,
  AuthCarousel,
  PreviewTimer,
  WalletCard,
  ThumbnailPreview,
  ThumbnailReviewSync,
  VideoPreview,
  AdminDataTable,
  DraggableTable,
  DismissableNotices,
  PhoneInputKe,
  BunnyTusUpload,
  OTPInput,
  DashboardGrid,
  SelectAll,
}

const csrfToken =
  document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? ""

const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: { _csrf_token: csrfToken },
  hooks,
})

topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" })
window.addEventListener("phx:page-loading-start", () => topbar.show(300))
window.addEventListener("phx:page-loading-stop", () => topbar.hide())

liveSocket.connect()
// @ts-ignore
window.liveSocket = liveSocket

if (import.meta.env.DEV) {
  window.addEventListener("phx:live_reload:attached", ({ detail: reloader }: CustomEvent) => {
    ;(reloader as any).enableServerLogs()

    let keyDown: string | null = null
    window.addEventListener("keydown", (e) => (keyDown = e.key))
    window.addEventListener("keyup", () => (keyDown = null))
    window.addEventListener(
      "click",
      (e) => {
        if (keyDown === "c") {
          e.preventDefault()
          e.stopImmediatePropagation()
          ;(reloader as any).openEditorAtCaller(e.target)
        } else if (keyDown === "d") {
          e.preventDefault()
          e.stopImmediatePropagation()
          ;(reloader as any).openEditorAtDef(e.target)
        }
      },
      true,
    )

    ;(window as any).liveReloader = reloader
  })
}
